import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FeUtil, API } from '../../../hezky-routes';
import { setLocationTree, setSenderLocations, setLocationList, setReceiverLocations, setExternalLocations, setSenderLocationsTree } from '../../../modules/location-tree/state/location-tree.actions';
import { saveAbbreviations, saveApplicationWiseCapabilities, saveEnvironments } from '../../../state/app.actions';
import { NzIconService } from 'ng-zorro-antd/icon';
import { selectAbbreviations, selectApplicationWiseCapabilities } from '../../../state/app.state';

@Injectable({
  providedIn: 'root'
})
export class CustomerService implements OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(private readonly feUtil: FeUtil, private readonly nzIconSvc: NzIconService) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // PRODUCT ENDPOINTS
  getAllProducts(): Observable<any> {
    return this.feUtil.httpSvc.get<any[]>(API.CUSTOMER.PRODUCT);
  }

  addProduct(data: any): Observable<any> {
    return this.feUtil.httpSvc.post<any[]>(API.CUSTOMER.PRODUCT, data);
  }

  getProductById(id: number): Observable<any> {
    return this.feUtil.httpSvc.get<any[]>(API.CUSTOMER.PRODUCT_BY_ID(id));
  }

  updateProduct(id: number, data: any): Observable<any> {
    return this.feUtil.httpSvc.patch<any[]>(API.CUSTOMER.PRODUCT_BY_ID(id), data);
  }
  
  // LOCATION ENDPOINTS
  fetchLocation(data: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.LOCATION, data);
  }

  addLocation(data: any): Observable<any> {
    return this.feUtil.httpSvc.post<any[]>(API.CUSTOMER.LOCATION, data);
  }

  updateLocation(locationId: number, data: any): Observable<any> {
    return this.feUtil.httpSvc.patch<any[]>(API.CUSTOMER.LOCATION_BY_ID(locationId), data)
  }

  // ALARM TEMPLATE ENDPOINTS
  addAlarmTemplate(data: any): Observable<any> {
    return this.feUtil.httpSvc.post<any[]>(API.CUSTOMER.ALARM, data)
  }

  public fetchAlarmTemplates(): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.ALARM);
  }

  public fetchUserByLocationId(location_id: number): Observable<any> {
    return this.feUtil.httpSvc.get(API.USER.GET_ALL_USER + `?location_id=${location_id}`);
  }

  // EXTERNAL LOCATION ENDPOINT
  public fetchQuickLocationById(location_id: number | string): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.GET_QUICK_LOCATION_BY_ID(location_id));
  }

  // ABBREVIATION ENDPOINT
  public fetchSensorTypeOptions(params: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.GET_SENSOR_TYPE_OPTIONS, params)
  }

  public validateSensorTypeAndAbbreviation(params: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.VALIDATE_SENSOR_TYPE_AND_ABBREVIATION, params)
  }

  // LOGISTIC / TRANSPORTER COMPANY ENDPOINTS
  public getAllTransporters(params: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.GET_LOGISTIC_COMPANIES, params);
  }

  public addTransporter(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.CUSTOMER.GET_LOGISTIC_COMPANIES, payload);
  }

  public updateTransporter(id: number, payload: any): Observable<any> {
    return this.feUtil.httpSvc.patch(API.CUSTOMER.UPDATE_LOGISTIC_COMPANY_BY_ID(id), payload);
  }

  // External Location Endpoints
  public fetchExternalLoc(): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.GET_QUICK_LOCATIONS);
  }

  public fetchExternalLocById(id: number | string): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.GET_QUICK_LOCATION_BY_ID(id));
  }

  public updateExternalLoc(id: number | string, data: any): Observable<any> {
    return this.feUtil.httpSvc.patch(API.CUSTOMER.GET_QUICK_LOCATION_BY_ID(id), data);
  }

  // Environment Endpoints
  public getEnvironments(): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.GET_ENVIRONMENTS);
  }

  // CUSTOMER ENDPOINTS
  public customerById(id: number): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.CUSTOMER_BY_ID(id));
  }
  
  public getCustomerConfig(): Observable<any> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.CUSTOMER_CONFIG);
  }

  // REASON FOR EXCURSION ENDPOINTS
  public reasons(): Observable<any[]> {
    return this.feUtil.httpSvc.get(API.CUSTOMER.REASON_FOR_EXCURSION)
  }

  // HELPER METHODS
  public getLocationTree(): void {
    const payload = { tree: true, byUser: true };
    this.fetchLocation(payload)
      .pipe(take(1))
      .subscribe({
        next: ({ status, data }) => {
          if (status !== 200) return;
          const locationTree = this.feUtil.convertToNzTree(data);
          const locationList = this.feUtil.convertNzTreeToList(locationTree);
          let receiverLocations = [...locationList];
          this.feUtil.store.dispatch(setLocationTree({ locationTree }));
          this.feUtil.store.dispatch(setLocationList({ locationList }));
          if (this.feUtil.isExtLocAllowed) {
            this.fetchExternalLoc()
              .pipe(take(1))
              .subscribe({
                next: ({ data }) => {
                  const extLocList = Array.isArray(data) ? data.map((item: any) => ({ label: item.display_name, value: item.location_id })) : [];
                  this.feUtil.store.dispatch(setExternalLocations({ externalLocations: extLocList }));
                  const mergedLocations = [...receiverLocations, ...extLocList].sort((a: any, b: any) => (a.label || '').localeCompare(b.label || ''));
                  this.feUtil.store.dispatch(setReceiverLocations({ receiverLocations: mergedLocations }));
                }
              });
          } else {
            this.feUtil.store.dispatch(setReceiverLocations({ receiverLocations }));
          }
        }
      });
  }

  public getSenderLocations() {
    // Fetch All customer level sender locations
    let customerPayload = { tree: true, is_sender_location: true };
    this.fetchLocation(customerPayload).pipe(take(1)).subscribe(({ status, data }) => {
      if (status !== 200) return;
      const senderLocTree = this.feUtil.convertToNzTree(data);
      const senderLocList = this.feUtil.convertNzTreeToList(senderLocTree);
      this.feUtil.store.dispatch(setSenderLocationsTree({ senderLocationTree: senderLocTree }));
      this.feUtil.store.dispatch(setSenderLocations({ senderLocations: senderLocList }));
    });
  }

  public customerConfig(): void {
    this.getCustomerConfig().pipe(take(1)).subscribe(({ status, data }) => {
      if (status !== 200) return;
      let config = this.feUtil.groupByKey(data, 'application_id');
      Object.entries(config).forEach(([_, items]: [string, any[]]) => {
        let dispatched = false;
        const capabilityNames = new Set<string>();
        let capabilities: any[] = [];
        let applicationId: number | null = null;
        items.forEach((item: any) => {
          const { value, application_id } = item;
          let parsedValue: any;
          try {
            parsedValue = JSON.parse(value);
          } catch {
            return;
          }
          if (
            Array.isArray(parsedValue) &&
            parsedValue.every((entry: any) => entry && typeof entry === 'object' && 'icon' in entry && 'name' in entry)
          ) {
            parsedValue.forEach((cap: any) => {
              if (!capabilityNames.has(cap.name)) {
                capabilityNames.add(cap.name);
                this.nzIconSvc.addIconLiteral(`hezky:${cap.name}`, cap.icon);
              }
            });
            capabilities = parsedValue;
            applicationId = Number(Array.isArray(application_id) ? application_id[0] : application_id);
          }
        });

        if (!dispatched && capabilities.length && applicationId !== null) {
          let currentCapabilities: any;
          this.feUtil.store.select(selectApplicationWiseCapabilities).pipe().subscribe((capabilitiesList: any) => {
            currentCapabilities = capabilitiesList?.[applicationId ?? 0];
          }).unsubscribe();
          if (
            !currentCapabilities || 
            JSON.stringify(currentCapabilities) !== JSON.stringify(capabilities)
          ) {
            this.feUtil.store.dispatch(
              saveApplicationWiseCapabilities({
                applicationId,
                capabilities
              })
            );
            dispatched = true;
          }
        }
      });
    });
  }

  public changeEnvironment() {
    this.getEnvironments().pipe(take(1)).subscribe(({ data }) => {
      const groupedEnv = this.feUtil.groupByKey(data, 'application_id');
      this.feUtil.store.dispatch(saveEnvironments({ environments: groupedEnv }));
    });
  }

  public getAbbreviations() {
    let currentAbbreviations: any;
    this.feUtil.store.select(selectAbbreviations).pipe().subscribe((abb) => currentAbbreviations = abb).unsubscribe();
    this.fetchSensorTypeOptions({ customer_id: this.feUtil.user_data.customer.customer_id }).pipe(take(1)).subscribe(({ data }) => {
      if (
        currentAbbreviations &&
        JSON.stringify(currentAbbreviations) === JSON.stringify(data)
      ) {
        return;
      }
      if (!data || data.length === 0) {
        return;
      }
      this.feUtil.store.dispatch(saveAbbreviations({ abbreviations: data }));
    });
  }
}
